import { useRef, useState } from 'react';
import { ProductImage } from '../../ui/ProductImage';
import { MediaView } from '../shared/MediaView';
import { founder, salon } from '../../data/content';
import { useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { productGallery } from '../../core/rules/media';
import type { Product, Treatment } from '../../core/types/catalog';
type Props = {
  product: Product;
  salonTreatment?: Treatment;
};

/**
 * Galerij op de productpagina: eerst de packshot (heel, op een rustig vlak), dan de extra beelden
 * uit het beheer, en tot slot altijd de salonfoto met waarom Sarah dit kiest.
 *
 * Telefoon: één beeld per scherm, vegen, met stipjes eronder. Computer: grote packshot en de rest
 * in een rustig raster eronder, zodat het koopblok ernaast mee kan scrollen.
 */
export function ProductGallery({
  product,
  salonTreatment
}: Props) {
  const {
    policy
  } = useSite();
  // De packshot staat hieronder apart (heel, niet uitgesneden); de rest zijn de extra beelden.
  const extra = productGallery(product, shopLabels.productImage(product)).slice(1);
  const slides = 1 + extra.length + 1;
  const stripRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const onScroll = () => {
    const el = stripRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth * 0.88));
    if (i !== current) setCurrent(Math.min(slides - 1, Math.max(0, i)));
  };
  return <div className="min-w-0" data-id="element-8072" data-mp="2evm0n9nmntsr">
      <div ref={stripRef} onScroll={onScroll} tabIndex={0} role="region" aria-label={`Foto's van ${product.name}`} className="-mx-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-6 [scrollbar-width:none] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay lg:mx-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-8073" data-mp="2ewnlu6ycxwo7">
        {/* De packshot: het grootst, en het eerste wat je ziet. */}
        <div className="aspect-[4/5] w-[88%] shrink-0 snap-center bg-shell lg:col-span-2 lg:aspect-[5/4] lg:w-full" data-id="element-8074" data-mp="b8txxkutkz7g">
          <ProductImage src={product.image} alt={shopLabels.productImage(product)} loading="eager" className="h-full w-full p-[9%] lg:p-[7%]" data-id="element-8075" data-mp="g7na99ydek61" />
        </div>

        {extra.map((m, i) => <MediaView key={`${i}-${m.image}`} media={m} sizes="(min-width: 1024px) 28vw, 88vw" className="aspect-[4/5] w-[88%] shrink-0 snap-center lg:w-full" data-id="element-8076" data-mp="q1nqa6v6x9tj" data-mpi={i} />)}

        <figure className={`relative w-[88%] shrink-0 snap-center lg:w-full ${extra.length % 2 === 0 ? 'lg:col-span-2' : ''}`} data-id="element-8077" data-mp="2dw2a60af8cf">
          <MediaView media={{
          image: founder.withProducts,
          alt: `${founder.name} in de salon in ${salon.city}`,
          focus: {
            x: 50,
            y: 35
          }
        }} sizes="(min-width: 1024px) 55vw, 88vw" className={`aspect-[4/5] w-full ${extra.length % 2 === 0 ? 'lg:aspect-[16/10]' : ''}`} data-id="element-8078" data-mp="75xmqf7m05dj" />
          <figcaption className="absolute bottom-4 left-4 right-4 bg-shell/95 p-4 text-[14px] leading-relaxed text-muted backdrop-blur-sm sm:right-auto sm:max-w-xs" data-id="element-8079" data-mp="1ryl1gs3rso24">
            {salonTreatment ? <>
                <span className="text-ink" data-id="element-8080" data-mp="1j0j3ymj8wdjl">In de salon</span> onderdeel van het thuisprotocol bij {salonTreatment.name}.
              </> : <>
                <span className="text-ink" data-id="element-8081" data-mp="191tkwqfm4n1r">Geselecteerd door {founder.firstName}</span>, na {policy.yearsExperience} jaar testen in de salon.
              </>}
          </figcaption>
        </figure>
      </div>

      {/* Stipjes op telefoon: waar je bent in de reeks. Decoratief; de strip zelf is te bedienen. */}
      <div className="mt-4 flex justify-center gap-1.5 lg:hidden" aria-hidden="true" data-id="element-8082" data-mp="v6r7zjcclxfg">
        {Array.from({
        length: slides
      }, (_, i) => <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-soft ${i === current ? 'w-5 bg-ink' : 'w-1.5 bg-ink/25'}`} data-id="element-8083" data-mp="la62yy0bwmbm" />)}
      </div>
    </div>;
}